/**
 * Recover from a stale build after a deploy.
 *
 * A tab opened before a deploy still references the previous build's hashed
 * chunks (e.g. CourseStartPageClassic-<oldhash>.js). They no longer exist on
 * the server, so the next lazy page load fails with "Failed to fetch
 * dynamically imported module". The fix is simply to load the new build: reload
 * the page once. A short guard prevents a reload loop if the failure is real.
 */
const RELOAD_KEY = 'ilearn_stale_build_reload_at';
const GUARD_MS = 60_000;

const CHUNK_ERROR = /Failed to fetch dynamically imported module|Importing a module script failed|error loading dynamically imported module|Unable to preload CSS|ChunkLoadError|Loading chunk [\w-]+ failed/i;

export function isStaleChunkError(error: unknown): boolean {
  const msg = (error as { message?: unknown })?.message ?? error;
  return CHUNK_ERROR.test(String(msg ?? ''));
}

/** Reload onto the new build once. Returns false if we already tried recently. */
export function reloadOnceForNewBuild(): boolean {
  try {
    const last = Number(sessionStorage.getItem(RELOAD_KEY) || 0);
    if (Date.now() - last < GUARD_MS) return false;
    sessionStorage.setItem(RELOAD_KEY, String(Date.now()));
  } catch {
    // no sessionStorage — still try once
  }
  window.location.reload();
  return true;
}

/** Vite fires this when a dynamic import's dependencies fail to preload. */
export function installStaleBuildRecovery(): void {
  window.addEventListener('vite:preloadError', (event) => {
    event.preventDefault();
    reloadOnceForNewBuild();
  });
}
